import React, { useEffect, useState } from "react";
import { FaEye, FaEdit, FaTrash, FaPlus, FaSearch, FaRedo } from "react-icons/fa";
import Swal from "../../utils/swalCompat";
import { useNavigate } from "react-router-dom";
import { mailList, deleteMail } from "../../api/endpoints";
import PageHeader from "../../components/PageHeader";

const MailList = () => {
  const navigate = useNavigate();

  const [mails, setMails] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [mailType, setMailType] = useState("");
  const [status, setStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [search, setSearch] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const fetchMails = async () => {
    try {
      setLoading(true);

      const params = { page, limit };

      if (mailType) params.mail_type = mailType;
      if (status) params.status = status;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (search.trim()) params.search = search.trim();

      const res = await mailList(params);
      console.log("Mail List Response:", res);

      const responseData = res.data || {};

      setMails(responseData.mails || []);
      setTotalPages(responseData.pagination?.totalPages || 1);
      setTotalItems(responseData.pagination?.total || 0);
    } catch (error) {
      console.error("Mail list error:", error);
      Swal.fire("Error", "Failed to fetch mails", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearch = () => {
    setPage(1);
    fetchMails();
  };

  const handleReset = () => {
    setMailType("");
    setStatus("");
    setStartDate("");
    setEndDate("");
    setSearch("");
    setPage(1);
    setTimeout(fetchMails, 0);
  };

  const handleDelete = async (id) => {
    const confirmResult = await Swal.fire({
      title: "Delete this mail?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirmResult.isConfirmed) return;

    try {
      await deleteMail(id);
      // alert(id);
      Swal.fire("Deleted!", "Mail has been deleted.", "success");
      fetchMails();
    } catch (error) {
      console.error("Delete mail error:", error);
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to delete mail",
        "error"
      );
    }
  };

  const getReceiverDisplay = (mail) => {
    if (mail.mail_type === "SYSTEM") {
      return "All Users";
    }
    if (mail.receiver_id) {
      return (
        mail.receiver_id.fullName ||
        mail.receiver_id.mobile ||
        mail.receiver_id.userId ||
        "Unknown User"
      );
    }
    return "N/A";
  };

  const getRewardDisplay = (mail) => {
    if (!mail.reward_type || mail.reward_type === "NONE") {
      return <span className="text-muted">-</span>;
    }
    return (
      <span className="badge bg-warning text-dark">
        {mail.reward_type} - {mail.reward_amount}
      </span>
    );
  };

  const getMailTypeBadge = (type) => {
    const map = {
      SYSTEM: "bg-primary",
      PERSONAL: "bg-orange text-dark",
    };
    return (
      <span className={`badge ${map[type] || "bg-secondary"}`}>{type}</span>
    );
  };

  const getStatusBadge = (status) => {
    const map = {
      ACTIVE: "bg-success",
      INACTIVE: "bg-secondary",
      DELETED: "bg-danger",
    };
    return (
      <span className={`badge ${map[status] || "bg-secondary"}`}>{status}</span>
    );
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisible = 5;

    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  return (
    <div className="mail-list-page lead-page">
      <PageHeader
        title="Mail List"
        subtitle="Manage system and personal mails"
        actions={
          <button
            className="btn btn-primary"
            onClick={() => navigate("/add-mails")}
          >
            <FaPlus className="me-2" />
            Add Mail
          </button>
        }
      />

      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-4">
          {/* Filters */}
          <div className="row g-2 mb-3 align-items-end">
            <div className="col-md-2">
              <label className="form-label fw-semibold small">Mail Type</label>
              <select
                className="form-select"
                value={mailType}
                onChange={(e) => setMailType(e.target.value)}
              >
                <option value="">All Mail Types</option>
                <option value="SYSTEM">System</option>
                <option value="PERSONAL">Personal</option>
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label fw-semibold small">Status</label>
              <select
                className="form-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
                <option value="DELETED">Deleted</option>
              </select>
            </div>

            <div className="col-md-2">
              <label className="form-label fw-semibold small">Start Date</label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div className="col-md-2">
              <label className="form-label fw-semibold small">End Date</label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            <div className="col-md-2">
              <label className="form-label fw-semibold small">Search</label>
              <input
                type="text"
                className="form-control"
                placeholder="Title or content..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>

            <div className="col-md-2 d-flex gap-2">
              <button className="btn btn-primary flex-fill" onClick={handleSearch}>
                <FaSearch className="me-1" />
                Search
              </button>
              <button className="btn btn-outline-secondary" onClick={handleReset}>
                <FaRedo />
              </button>
            </div>
          </div>

          <div className="mb-3">
            <span className="text-muted">
              Showing {mails.length} of {totalItems} mails
            </span>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Title</th>
                  <th>Mail Type</th>
                  <th>Receiver</th>
                  <th>Reward</th>
                  <th>Status</th>
                  <th>Created At</th>
                  <th>Expires At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="text-center">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : mails.length > 0 ? (
                  mails.map((mail, index) => (
                    <tr key={mail._id}>
                      <td>{(page - 1) * limit + index + 1}</td>
                      <td>{mail.title}</td>
                      <td>{getMailTypeBadge(mail.mail_type)}</td>
                      <td>{getReceiverDisplay(mail)}</td>
                      <td>{getRewardDisplay(mail)}</td>
                      <td>{getStatusBadge(mail.status)}</td>
                      <td>{new Date(mail.createdAt).toLocaleString()}</td>
                      <td>
                        {mail.expires_at
                          ? new Date(mail.expires_at).toLocaleString()
                          : "-"}
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          {/* <button
                            className="btn btn-sm btn-info text-white"
                            title="View"
                            onClick={() => navigate(`/mail/view/${mail._id}`)}
                          >
                            <FaEye />
                          </button>
                          <button
                            className="btn btn-sm btn-warning text-white"
                            title="Edit"
                            onClick={() => navigate(`/mail/edit/${mail._id}`)}
                          >
                            <FaEdit />
                          </button> */}
                          <button
                            className="btn btn-sm btn-danger"
                            title="Delete"
                            onClick={() => handleDelete(mail._id)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center">
                      No mails found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center gap-2 mt-4">
              <button
                className="btn btn-outline-secondary"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
              >
                Previous
              </button>

              <div className="d-flex gap-1">
                {getPageNumbers().map((pageNum) => (
                  <button
                    key={pageNum}
                    className={`btn ${
                      pageNum === page ? "btn-primary" : "btn-outline-primary"
                    }`}
                    onClick={() => setPage(pageNum)}
                    disabled={loading}
                    style={{ minWidth: "40px" }}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              <button
                className="btn btn-outline-secondary"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
              >
                Next
              </button>
            </div>
          )}

          <div className="d-flex justify-content-center mt-2">
            <small className="text-muted">
              Page {page} of {totalPages}
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MailList;